import type { ServicePageData } from './types';

const pavementSigns: ServicePageData = {
  slug: 'pavement-signs',
  eyebrow: 'Signage',
  title: 'Stand out and get noticed with',
  titleAccent: 'custom pavement signs.',
  intro:
    'Our pavement signs come in a range of styles and sizes. Free-standing signs that capture passing footfall and drive customers through your door.',
  heroImages: [
    '/home/pavement-signs/pavement-sign-petalbar.jpg',
    '/home/pavement-signs/pavement-sign-mockup.jpg',
    '/home/pavement-signs/pavement-sign-atelier.jpg',
    '/home/pavement-signs/pavement-sign-hkh.jpg',
    '/home/pavement-signs/pavement-sign-wildjac.jpg',
    '/home/pavement-signs/pavement-sign-salon4.jpg',
    '/home/pavement-signs/pavement-sign-tattoo.jpg',
    '/home/pavement-signs/swinger-pavement-sign-portrait.jpg',
    '/home/pavement-signs/pavement-sign-atelier.jpg',
    '/home/pavement-signs/pavement-sign-petalbar.jpg',
    '/home/pavement-signs/pavement-sign-salon4.jpg',
    '/home/pavement-signs/pavement-sign-hkh.jpg',
    '/home/pavement-signs/pavement-sign-mockup.jpg',
    '/home/pavement-signs/pavement-sign-tattoo.jpg',
    '/home/pavement-signs/pavement-sign-wildjac.jpg',
    '/home/pavement-signs/pavement-sign-petalbar.jpg',
    '/home/pavement-signs/pavement-sign-atelier.jpg',
    '/home/pavement-signs/swinger-pavement-sign-portrait.jpg',
    '/home/pavement-signs/pavement-sign-salon4.jpg',
    '/home/pavement-signs/pavement-sign-hkh.jpg',
    '/home/pavement-signs/pavement-sign-tattoo.jpg',
  ],

  useCases: [
    { title: 'Retail stores', text: 'Announce promotions, new arrivals, or simply mark your presence on the high street.' },
    { title: 'Cafes and restaurants', text: 'Daily specials, happy hour offers and menu highlights to entice passers-by.' },
    { title: 'Salons and studios', text: 'Point walk-ins to the door when your shopfront is set back from the street.' },
    { title: 'Events and markets', text: 'Portable, lightweight options for trade shows, farmers markets and festivals.' },
    { title: 'Rebranding an existing frame', text: 'Already have a frame? Drop it off and we will fit new customisation.' },
  ],

  optionsHeading: 'Choose your pavement sign',
  optionsIntro:
    'Our pavement signs come in a range of styles and sizes. They are built to withstand all weathers, but some styles are better suited to high wind than others.',
  options: [
    {
      id: 'eco-swing',
      title: 'Eco Swing pavement sign',
      lead: 'Our bestselling pavement sign, best for windy locations.',
      body: 'The swinging panel moves with the wind, reducing the chance of toppling. Steel tubing frame in black or white on a black recycled PVC base. The large base is water fillable with wheels, and panels are easy to swap out.',
      image: '/home/pavement-signs/swinger-pavement-sign-portrait.jpg',
      specs: [
        { label: 'Panel sizes', value: 'Small 430 × 625 mm, medium 500 × 750 mm, large 588 × 917 mm.' },
        { label: 'Weights', value: 'Small 11 kg, medium 16 kg, large 13 kg empty or 31 kg with water.' },
        { label: 'Panel', value: 'Aluminium, double sided. Easily swapped for new graphics.' },
        { label: 'Frame', value: 'Steel tubing in black or white. Black recycled PVC base.' },
      ],
    },
    {
      id: 'a-board',
      title: 'A-board',
      lead: 'The classic folding pavement sign.',
      body: 'Two panels hinged at the top that fold flat for storage. Light enough to carry in at closing time and a good fit for sheltered doorways and covered walkways.',
      image: '/home/pavement-signs/pavement-sign-mockup.jpg',
      specs: [
        { label: 'Panels', value: 'Double sided, customised with printed or cut vinyl.' },
        { label: 'Best for', value: 'Sheltered spots. Choose the Eco Swing for exposed or windy locations.' },
      ],
    },
    {
      id: 'existing-frame',
      title: 'Rebrand your existing frame',
      lead: 'Already have a frame but need new customisation?',
      body: 'Drop off your pavement sign to us for our design team to look at. We will fit new panels or re-vinyl the existing ones so your frame matches your current branding.',
      image: '/home/pavement-signs/pavement-sign-hkh.jpg',
      specs: [
        { label: 'Panels', value: 'Replacement panels available for most standard frames.' },
        { label: 'Drop off', value: 'Bring the frame to our unit in Kidderminster.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Printed vinyl',
      text: 'Inks are used to print a design onto clear or white vinyl. Ideal for multiple colours, gradients, shading, photographs and complex illustrations. Because the ink sits on the surface, colour can fade quicker than cut vinyl.',
    },
    {
      title: 'Cut vinyl',
      text: 'A specialist machine follows the design with a sharp blade, cutting shapes and letters from pre-coloured rolls of vinyl. Sharper edges than print, with no background. Best for simple designs, logos and lettering.',
    },
  ],

  gallery: [
    '/home/pavement-signs/pavement-sign-petalbar.jpg',
    '/home/pavement-signs/pavement-sign-atelier.jpg',
    '/home/pavement-signs/pavement-sign-hkh.jpg',
    '/home/pavement-signs/pavement-sign-wildjac.jpg',
    '/home/pavement-signs/pavement-sign-salon4.jpg',
    '/home/pavement-signs/pavement-sign-tattoo.jpg',
    '/home/pavement-signs/pavement-sign-mockup.jpg',
  ],

  steps: [
    {
      title: 'Get a free quote',
      text: 'Choose from our range of pavement signs or A-boards. Already have a frame but need new customisation? Drop off your pavement sign for our design team to look at.',
    },
    {
      title: 'Design stage',
      text: 'Already have artwork? Send it over for a proof. Need help? Send your logo and our design team will put together a visual of how your pavement sign could look.',
    },
    {
      title: 'Approve your order',
      text: 'After you approve your order we get it into production, which usually takes 5 to 10 working days. Got a deadline? Chat to our team and we will do our best.',
    },
    {
      title: 'Order up',
      text: 'We will let you know when your order is ready to collect from our shop in Kidderminster, or we can arrange posting it to you.',
    },
  ],

  info: [
    {
      title: 'Built to last',
      text: 'All frames are manufactured from high-quality materials and built to withstand exterior conditions. Tell us about wind exposure and we will match the right frame to your location.',
      bullets: ['Replacement panels available', 'Reusable across campaigns', 'Guy ropes and stakes for extra stability'],
    },
    {
      title: 'Collection and delivery',
      text: 'Pavement signs are supplied ready to use. Collect from our shop in Kidderminster or have it posted to you.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Turnaround',
      text: 'Production usually takes 5 to 10 working days from approval. Need it sooner? Chat to our team and we will do our best to have everything ready in time.',
    },
  ],

  cta: {
    title: 'Get your free pavement sign quote.',
    text: 'Tell us about your business and we will recommend the right frame, panel and customisation for your location and budget.',
    image: '/home/pavement-signs/pavement-sign-atelier.jpg',
  },
};

export default pavementSigns;
